import { type User } from '@kalos/kalos-rpc';

export const lodgingAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'LodgingAdmin');
};
export const permitAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'PermitAdmin');
};

export const creditCardsPagePermissionCheck = (user: User) => {
  return !!user.permissionGroups.find((item) => item.name === 'AccountingAdmin');
};

export const isTransactionPreValidatePermissionCheck = (user: User) => {
  return !!user.permissionGroups.find((item) => item.name === 'TransactionValidator');
};

export const analyticsAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'AnalyticsAdmin');
};

export const newHireDashboardCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'NewHireDashboardTester');
};

export const terminationDashboardCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'NewFireDashboardTester');
};

export const hrCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'HR');
};

export const kpiAccessCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'KPIAccess');
};

export const customerAccountAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'CustomerAccountAdmin');
};

// Two separate membership gates: creating a commercial contract is reversible, approving one
// activates memberships and billing. Seeded by migration 404; both start with no members.
export const membershipCommercialPlansCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'MembershipCommercialPlans');
};

export const projectCoordinatorCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'ProjectCoordinator');
};

/** Publishers draft and publish the project schedule for the departments they hold. */
export const projectCoordinatorPublisherCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'ProjectCoordinatorPublisher');
};

/** Coordinators and publishers both open Project Scheduling. */
export const projectScheduleAccessCheck = (user: User) =>
  projectCoordinatorCheck(user) || projectCoordinatorPublisherCheck(user);

/** Opens Project Scheduling: coordinators and publishers work the board, the lodging desk reads the Overview. */
export const projectScheduleOpenCheck = (user: User) =>
  projectScheduleAccessCheck(user) || lodgingAdminCheck(user);

/** The lodging desk without scheduler access: the Overview only, read-only. */
export const projectScheduleLodgingDeskCheck = (user: User) =>
  lodgingAdminCheck(user) && !projectScheduleAccessCheck(user);

export const membershipAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'MembershipAdmin');
};

export const customerFinancialAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'CustomerFinancialAdmin');
};

// Seeded by migration 433, with no members. Its own gate rather than a reuse of
// an existing admin role because a wrong ServiceChannel mapping does not error:
// it silently files real commercial work into the wrong department.
export const serviceChannelAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'ServiceChannelAdmin');
};

// Seeded by migration 649, with no members. Engineers: may switch any feature flag and
// assign each flag's owners. A flag's owners (feature_flag_owner rows) may switch only
// their own flags without it.
export const featureFlagAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'FeatureFlagAdmin');
};

// Privilege 116. Its own gate rather than a reuse of HR/Payroll: the org chart's
// admin screen rewrites the shape of the chart for everyone, which is a
// different thing from being able to see employee records.
export const orgChartAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'OrgChartAdmin');
};

export const permissionsManagerCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'PermissionManager');
};

export const proposalAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'ProposalAdmin');
};

export const accountingAdminCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'AccountingAdmin');
};

export const serviceCallBillingAccessCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'ServiceCallBillingAccess');
};

export const devTestingCheck = (user: User) => {
  return !!user.permissionGroups.find((el) => el.name === 'DevTesting');
};
export const managerCheck = (user: User) => {
  return user.permissionGroups.some((el) => el.name === 'Manager');
};

/** Reports side menu and base /reports access (internal report tiles use additional checks). */
export const reportsMenuAccessCheck = (user: User) => {
  if (!user?.id) return false;
  return (
    !!user.permissionGroups.find((p) => p.name === 'ReportPermission') ||
    user.permissionGroups.some((p) => p.name === 'Manager') ||
    user.permissionGroups.some((p) => p.name === 'Payroll') ||
    user.isSU === 1 ||
    user.isAdmin === 1
  );
};

export const hasPayrollPermission = (user: User) =>
  user.permissionGroups.some(
    (p) => (p.type === 'role' || p.type === 'privilege') && p.name === 'Payroll',
  );

const hasAccountingReportPermission = (user: User) =>
  user.permissionGroups.some(
    (p) =>
      (p.type === 'role' || p.type === 'privilege') &&
      (p.name === 'AccountingAdmin' ||
        p.name === 'AccountsPayable' ||
        p.name === 'AccountsPayableAdmin' ||
        p.name === 'AccountingAcceptOverride' ||
        p.name === 'TransactionValidator'),
  );

const hasReportSUPermission = (user: User) =>
  user.permissionGroups.some((p) => p.name === 'ReportSU');

/** Mirrors the per-section gates inside the /reports page. */
export const canViewPayrollReports = (user: User) =>
  hasReportSUPermission(user) || hasPayrollPermission(user);

export const canViewAccountingReports = (user: User) =>
  hasReportSUPermission(user) || hasAccountingReportPermission(user);

export const canViewInternalReports = (user: User) =>
  hasReportSUPermission(user) ||
  user.permissionGroups.some((p) => p.name === 'Manager') ||
  hasAccountingReportPermission(user) ||
  hasPayrollPermission(user) ||
  user.permissionGroups.some((p) => p.name === 'ReportPermission');

export const dispatchCheck = (user: User) => {
  return !!user.permissionGroups.find((p) => p.name === 'Dispatch');
};
